import React, { useEffect, useState } from 'react';
import { Loader, X } from 'react-feather';

import { CustomerDetailModalProps } from '@/Users/types/userTypes';
import { useUserMutations } from '../../hooks/useUserMutations';

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({
  onClose,
  userDetail,
}) => {
  const [formData, setFormData] = useState({
    companyName: '',
    companyAddress: '',
    representativeName: '',
    phoneNumber: '',
    businessRegistrationNumber: '',
    businessCertificateUrl: '',
    postalCode: '',
  });

  const { updateUserDetail, isUpdatingUserDetail } = useUserMutations(
    userDetail.uuid,
  );

  useEffect(() => {
    setFormData({
      companyName: userDetail.companyName || '',
      companyAddress: userDetail.companyAddress || '',
      representativeName: userDetail.representativeName || '',
      phoneNumber: userDetail.phoneNumber || '',
      businessRegistrationNumber: userDetail.businessRegistrationNumber || '',
      businessCertificateUrl: userDetail.businessCertificateUrl || '',
      postalCode: userDetail.postalCode || '',
    });
  }, [userDetail]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    updateUserDetail(formData);
    onClose(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-detail-modal-title"
    >
      <div className="w-[32.5rem] rounded-lg bg-white text-center">
        <div className="flex w-full items-center justify-between p-6">
          <h3 className="text-xl font-medium" id="customer-detail-modal-title">
            Edit Customer
          </h3>
          <button
            type="button"
            className="cursor-pointer bg-transparent"
            onClick={() => onClose(false)}
            aria-label="Close customer detail modal"
            disabled={isUpdatingUserDetail}
          >
            <X size={20} className="text-[#707070]" aria-hidden="true" />
          </button>
        </div>

        <div className="rounded-b-lg bg-[#f1f1f1] p-6 text-left">
          {isUpdatingUserDetail && (
            <div className="mb-4 flex items-center justify-center rounded bg-[#f9f9f9] p-4">
              <Loader size={16} className="mr-2 animate-spin text-[#3e3e3e]" />
              <span className="text-sm text-[#666]">
                Updating user details...
              </span>
            </div>
          )}

          <form
            className="flex flex-col gap-4"
            aria-labelledby="customer-detail-modal-title"
          >
            <div className="flex items-center justify-between">
              <label htmlFor="businessRegNum" className="text-sm font-normal">
                Business Registration Number
              </label>
              <input
                id="businessRegNum"
                type="text"
                value={formData.businessRegistrationNumber}
                onChange={(e) =>
                  handleInputChange(
                    'businessRegistrationNumber',
                    e.target.value,
                  )
                }
                className="h-8 w-[16.75rem] rounded border border-[#cecece] pl-2 text-sm text-[#444] focus:border-[#3e3e3e] focus:outline-none disabled:bg-[#f9f9f9] disabled:opacity-50"
                aria-label="Business Registration Number"
                maxLength={100}
                disabled={isUpdatingUserDetail}
              />
            </div>

            <div className="flex items-center justify-between">
              <label htmlFor="companyName" className="text-sm font-normal">
                Company Name
              </label>
              <input
                id="companyName"
                type="text"
                value={formData.companyName}
                onChange={(e) =>
                  handleInputChange('companyName', e.target.value)
                }
                className="h-8 w-[16.75rem] rounded border border-[#cecece] pl-2 text-sm text-[#444] focus:border-[#3e3e3e] focus:outline-none disabled:bg-[#f9f9f9] disabled:opacity-50"
                aria-label="Company Name"
                maxLength={100}
                disabled={isUpdatingUserDetail}
              />
            </div>

            <div className="flex items-center justify-between">
              <label
                htmlFor="representativeName"
                className="text-sm font-normal"
              >
                Representative Name
              </label>
              <input
                id="representativeName"
                type="text"
                value={formData.representativeName}
                onChange={(e) =>
                  handleInputChange('representativeName', e.target.value)
                }
                className="h-8 w-[16.75rem] rounded border border-[#cecece] pl-2 text-sm text-[#444] focus:border-[#3e3e3e] focus:outline-none disabled:bg-[#f9f9f9] disabled:opacity-50"
                aria-label="Representative Name"
                maxLength={100}
                disabled={isUpdatingUserDetail}
              />
            </div>

            <div className="flex items-center justify-between">
              <label htmlFor="phoneNumber" className="text-sm font-normal">
                Phone Number
              </label>
              <input
                id="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) =>
                  handleInputChange('phoneNumber', e.target.value)
                }
                className="h-8 w-[16.75rem] rounded border border-[#cecece] pl-2 text-sm text-[#444] focus:border-[#3e3e3e] focus:outline-none disabled:bg-[#f9f9f9] disabled:opacity-50"
                aria-label="Phone Number"
                maxLength={20}
                placeholder="Numbers, +, (), -, spaces allowed"
                disabled={isUpdatingUserDetail}
              />
            </div>

            <div className="flex items-center justify-between">
              <label htmlFor="postalCode" className="text-sm font-normal">
                Postal Code
              </label>
              <input
                id="postalCode"
                type="text"
                value={formData.postalCode}
                onChange={(e) =>
                  handleInputChange('postalCode', e.target.value)
                }
                className="h-8 w-[16.75rem] rounded border border-[#cecece] pl-2 text-sm text-[#444] focus:border-[#3e3e3e] focus:outline-none disabled:bg-[#f9f9f9] disabled:opacity-50"
                aria-label="Postal Code"
                maxLength={50}
                disabled={isUpdatingUserDetail}
              />
            </div>

            <div className="flex items-center justify-between">
              <label htmlFor="address" className="text-sm font-normal">
                Address
              </label>
              <input
                id="address"
                type="text"
                value={formData.companyAddress}
                onChange={(e) =>
                  handleInputChange('companyAddress', e.target.value)
                }
                className="h-8 w-[16.75rem] rounded border border-[#cecece] pl-2 text-sm text-[#444] focus:border-[#3e3e3e] focus:outline-none disabled:bg-[#f9f9f9] disabled:opacity-50"
                aria-label="Address"
                maxLength={255}
                disabled={isUpdatingUserDetail}
              />
            </div>

            <div className="flex items-center justify-between">
              <label
                htmlFor="businessCertificateUrl"
                className="text-sm font-normal"
              >
                Business Certificate URL
              </label>
              <input
                id="businessCertificateUrl"
                type="url"
                value={formData.businessCertificateUrl}
                onChange={(e) =>
                  handleInputChange('businessCertificateUrl', e.target.value)
                }
                className="h-8 w-[16.75rem] rounded border border-[#cecece] pl-2 text-sm text-[#444] focus:border-[#3e3e3e] focus:outline-none disabled:bg-[#f9f9f9] disabled:opacity-50"
                aria-label="Business Certificate URL"
                maxLength={2048}
                placeholder="https://example.com/certificate"
                disabled={isUpdatingUserDetail}
              />
            </div>
          </form>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="h-7 cursor-pointer rounded bg-[#666] px-3 text-sm font-medium text-white hover:bg-[#444] disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => onClose(false)}
              disabled={isUpdatingUserDetail}
              aria-label="Cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              className="h-7 cursor-pointer rounded bg-[#3e3e3e] px-3 text-sm font-medium text-white hover:bg-[#222] disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleSave}
              disabled={isUpdatingUserDetail}
              aria-label="Save user details"
            >
              {isUpdatingUserDetail ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailModal;
